import { CARE_NECESSITIES, REQUEST_FORM_FIELDS } from "core/consts";
import { predicamentSchema } from "core/schemas/shared";
import {
  createMaxLengthMessage,
  createSimultaneousSchema,
} from "core/schemas/utils";
import { TransitionalCareFormData, ZodValidationIssue } from "core/types";
import {
  TEXT_AREA_MAX_CHARACTERS,
  TEXT_AREA_MAX_CHARACTERS_DESCRIPTION,
} from "dsl/ecosystems/PatientForms/components";
import {
  validatePatientConfirmation,
  validateTransitionalCareFormDates,
} from "dsl/ecosystems/PatientForms/TransitionalCare/Form/utils";
import Translations from "translations/types";
import { z } from "zod";

type TcareValidatorResult = true | Record<string, { customMessage: string }>;

function validationResultToIssues(
  result: TcareValidatorResult,
): ZodValidationIssue[] {
  if (result === true) return [];
  return Object.entries(result).map(([fieldName, { customMessage }]) => ({
    message: customMessage,
    path: [fieldName],
  }));
}

export const TransitionalCareRequestSchema = (translations: Translations) =>
  z.object({
    edited: z.boolean(),
    id: z.number(),
    manual: z.boolean(),
    [REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ADDRESS]: z.string().min(1, {
      message:
        translations.patientForms.transitionalCareForm.validationErrorMessage,
    }),
    [REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_CITY]: z.string().min(1, {
      message:
        translations.patientForms.transitionalCareForm.validationErrorMessage,
    }),
    [REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ZIPCODE]: z.string().min(1, {
      message:
        translations.patientForms.transitionalCareForm.validationErrorMessage,
    }),
    [REQUEST_FORM_FIELDS.CAREPROVIDER_NAME]: z.string().min(1, {
      message:
        translations.patientForms.transitionalCareForm.validationErrorMessage,
    }),
    [REQUEST_FORM_FIELDS.REQUEST_SENT_DATE]: z
      .number()
      .nullable()
      .refine((val) => val !== null, {
        message:
          translations.patientForms.transitionalCareForm.validationErrorMessage,
      }),
    [REQUEST_FORM_FIELDS.REQUEST_SENT_TIME]: z
      .union([
        z
          .object({
            hour: z.number().min(0).max(23),
            millisecond: z.number().min(0).max(999).default(0),
            minute: z.number().min(0).max(59),
            second: z.number().min(0).max(59).default(0),
          })
          .refine(
            (data) =>
              data.hour != null && data.minute != null && data.second != null,
            {
              message:
                translations.patientForms.transitionalCareForm
                  .validationErrorMessage,
            },
          ),
        z.null(),
      ])
      .refine((val) => val !== null, {
        message:
          translations.patientForms.transitionalCareForm.validationErrorMessage,
      }),
    [REQUEST_FORM_FIELDS.REQUEST_STATUS]: z
      .union([
        z.array(z.number()),
        z.array(z.union([z.string()])),
        z.number(),
        z.string(),
      ])
      .nullable()
      .transform((val): number[] | null => {
        if (val == null) return null;
        const arr = Array.isArray(val) ? val : [val];
        return arr.map((v) => (typeof v === "string" ? parseInt(v, 10) : v));
      })
      .refine((val) => val != null && val.length > 0, {
        message:
          translations.patientForms.transitionalCareForm.validationErrorMessage,
      }),
    request_id: z.number().nullable(),
  });

export function createTransitionalCareFormSchema(translations: Translations) {
  return z.object({
    aftercare_application_date: z.number().optional(),
    aftercare_application_explanation: z
      .string()
      .max(
        TEXT_AREA_MAX_CHARACTERS,
        createMaxLengthMessage(translations, TEXT_AREA_MAX_CHARACTERS),
      )
      .optional(),
    aftercare_application_submitted: z.boolean().optional(),
    aftercare_care_level_aftercare_required: z.boolean().optional(),
    aftercare_care_level_applied: z.boolean().optional(),
    aftercare_care_level_applied_date: z.number().optional(),
    aftercare_care_level_available: z.boolean().optional(),
    aftercare_care_level_explanation: z
      .string()
      .max(
        TEXT_AREA_MAX_CHARACTERS,
        createMaxLengthMessage(translations, TEXT_AREA_MAX_CHARACTERS),
      )
      .optional(),
    aftercare_medical_rehab: z.boolean().optional(),
    aftercare_mobile: z.boolean().optional(),
    aftercare_shortterm_care: z.boolean().optional(),
    aftercare_start_date: z.number().optional(),
    care_necessity: z.nativeEnum(CARE_NECESSITIES).optional(),
    clinic_ik_number: z.string().optional(),
    clinic_location_city: z.string().optional(),
    clinic_location_street: z.string().optional(),
    clinic_location_street_number: z.string().optional(),
    clinic_location_zipcode: z.string().optional(),
    clinic_name: z.string().optional(),
    handover_to_insurance_date: z.number().optional(),
    insurance_form_sent_date: z.number().optional(),
    internet_portal_used: predicamentSchema.optional(),
    legal_representative_consent_provided: z.boolean().optional(),
    legal_representative_first_name: z.string().optional(),
    legal_representative_last_name: z.string().optional(),
    legal_representative_location_city: z.string().optional(),
    legal_representative_location_street: z.string().optional(),
    legal_representative_location_street_number: z.string().optional(),
    legal_representative_location_zipcode: z.string().optional(),
    patient_date_of_birth: z.number().optional(),
    patient_first_name: z.string().optional(),
    patient_insurance_number: z.string().optional(),
    patient_last_name: z.string().optional(),
    patient_location_city: z.string().optional(),
    patient_location_house_number: z.string().optional(),
    patient_location_street: z.string().optional(),
    patient_location_zipcode: z.string().optional(),
    prior_treatment_end_date: z.number().optional(),
    prior_treatment_hospital_id: z.string().optional(),
    prior_treatment_start_date: z.number().optional(),
    request_list: z
      .array(TransitionalCareRequestSchema(translations))
      .optional(),
    social_worker_email: z.string().optional(),
    social_worker_fax: z.string().optional(),
    social_worker_first_name: z.string().optional(),
    social_worker_last_name: z.string().optional(),
    social_worker_telephone: z.string().optional(),
    transitional_care_department_code: z.string().optional(),
    transitional_care_description: z
      .string()
      .max(
        TEXT_AREA_MAX_CHARACTERS_DESCRIPTION,
        createMaxLengthMessage(
          translations,
          TEXT_AREA_MAX_CHARACTERS_DESCRIPTION,
        ),
      )
      .optional(),
    transitional_care_end_date: z.number().optional(),
    transitional_care_location_id: z.string().optional(),
    transitional_care_patient_confirmation: z.boolean().optional(),
    transitional_care_patient_confirmation_date: z.number().optional(),
    transitional_care_patient_confirmation_explanation: z
      .string()
      .max(
        TEXT_AREA_MAX_CHARACTERS,
        createMaxLengthMessage(translations, TEXT_AREA_MAX_CHARACTERS),
      )
      .optional(),
    transitional_care_start_date: z.number().optional(),
    transitional_care_start_search_date: z.number().optional(),
    transitional_care_suitable_providers_count: z.preprocess((val) => {
      const value = Array.isArray(val) ? val[0] : val;
      if (value === undefined || value === null || value === "") {
        return undefined;
      }
      return Number(value);
    }, z.number().optional()),
    transitional_care_suitable_providers_more_than_twenty: z
      .boolean()
      .optional(),
  });
}

export function createTransitionalCareFormValidator(
  translations: Translations,
): (data: TransitionalCareFormData) => ZodValidationIssue[] {
  return (data) => {
    const issues: ZodValidationIssue[] = [];
    const props = { translations };

    const dateValidationResult = validateTransitionalCareFormDates(
      data,
      props,
    ) as TcareValidatorResult;
    issues.push(...validationResultToIssues(dateValidationResult));

    const patientConfirmationResult = validatePatientConfirmation(data, props);
    issues.push(...validationResultToIssues(patientConfirmationResult));

    return issues;
  };
}

export function getTransitionalCareFormSchema(translations: Translations) {
  const validator = createTransitionalCareFormValidator(translations);
  return createSimultaneousSchema(
    createTransitionalCareFormSchema(translations),
    validator,
  );
}
